import { Calendar } from "lucide-react";
import { useState } from "react";
import { useIsMobile } from "src/hooks/useMobile";
import { Button } from "src/components/ui/button";
import { DropdownMenu, DropdownMenuContent, DropdownMenuTrigger } from "src/components/ui/dropdown-menu";
import { getMonthBounds } from "../utils/date-utils";

interface DateRangeModalProps {
  disclaimer?: string;
  className: string;
  selectedClassName: string;
  selectedDate: string;
  label?: string;
  selectedStartDate?: string;
  selectedEndDate?: string;
  presetRanges?: { key: string, label: string }[];
  onDateChange: (dateFilter: string, startDate: string, endDate: string) => void;
  onPresetSelect?: (filter: string) => {startDate: Date, endDate: Date};
}
const DateRangeModal: React.FC<DateRangeModalProps> = ({
  disclaimer,
  className,
  selectedClassName,
  selectedDate,
  label,
  selectedStartDate,
  selectedEndDate,
  presetRanges = [
    { key: 'yesterday', label: 'Yesterday' },
    { key: 'last_3_days', label: 'Last 3 days' },
    { key: 'last_week', label: 'Last week' },
    { key: 'last_month', label: 'Last month' },
    { key: 'last_quarter', label: 'Last quarter' },
    { key: 'this_month', label: 'This month' },
    { key: 'year_to_date', label: 'Year to date' },
  ],
  onPresetSelect,
  onDateChange,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedPreset, setSelectedPreset] = useState<string | null>(null);
  const [startDate, setStartDate] = useState(selectedStartDate || '');
  const [endDate, setEndDate] = useState(selectedEndDate || '');
  const isMobile = useIsMobile();

  const handlePresetSelect = (preset: string) => {
    setSelectedPreset(preset);
    if (onPresetSelect) {
      const { startDate, endDate } = onPresetSelect(preset);
      setStartDate(formatDateForInput(startDate));
      setEndDate(formatDateForInput(endDate));
    }
    else {

      const now = new Date();
      let start: Date;
      let end: Date;

      switch (preset) {
        case 'yesterday':
          start = new Date(now);
          start.setDate(now.getDate() - 1);
          end = new Date(start);
          break;

        case 'last_3_days':
          start = new Date(now);
          start.setDate(now.getDate() - 3);
          end = new Date(now);
          break;

        case 'last_week':
          start = new Date(now);
          start.setDate(now.getDate() - 7);
          end = new Date(now);
          break;

        case 'last_month':
          start = new Date(now);
          start.setMonth(now.getMonth() - 1);
          end = new Date(now);
          break;

        case 'last_quarter':
          start = new Date(now);
          start.setMonth(now.getMonth() - 3);
          end = new Date(now);
          break;

        case 'this_month':
          start = getMonthBounds(now).first;
          end = getMonthBounds(now).last;
          break;

        case 'year_to_date':
          start = new Date(now);
          start.setMonth(0);
          start.setDate(1);
          end = new Date(now);
          break;

        default:
          start = new Date(now);
          end = new Date(now);
      }

      setStartDate(formatDateForInput(start));
      setEndDate(formatDateForInput(end));
    }
  };

  // Helper function to ensure timezone-safe date handling
  const formatDateForInput = (date: Date): string => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  // Helper function to parse date input and create timezone-safe date
  const parseDateInput = (dateString: string): Date => {
    // Create date in local timezone to avoid UTC conversion issues
    const [year, month, day] = dateString.split('-').map(Number);
    return new Date(year, month - 1, day); // month is 0-indexed
  };

  const handleSubmit = () => {
    if (selectedPreset) {
      onDateChange(selectedPreset, startDate, endDate);
    } else if (startDate && endDate) {
      // Ensure dates are handled in local timezone
      const start = parseDateInput(startDate);
      const end = parseDateInput(endDate);

      // Format dates consistently for the callback
      const formattedStart = formatDateForInput(start);
      const formattedEnd = formatDateForInput(end);
      onDateChange('custom', formattedStart, formattedEnd);
    }
    setIsOpen(false);
  };


  return (
    <DropdownMenu
      open={isOpen}
      onOpenChange={setIsOpen}
    >
      <DropdownMenuTrigger asChild>
        <Button
          className={`${className} hover:opacity-65 
          ${!['last_month', 'this_month', 'last_week'].includes(selectedDate) && selectedDate !== '' ? selectedClassName : ''}`}
          variant="ghost"
          size="med"
        >
          <Calendar className="w-4 h-4" />
          {label && label}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        className="w-[500px] p-0 max-h-[80vh] overflow-y-auto"
        align="end"
        side="bottom"
        sideOffset={4}
        avoidCollisions={true}
        collisionBoundary={document.body}
      >
        {disclaimer && (
          <div className="bg-gray-50 p-4 border-b border-gray-201">
            <p className="text-sm text-gray-700">
              {disclaimer}
            </p>
          </div>
        )}
        <div className="flex">
          {/* Left side - Preset ranges */}
          <div className="w-[170px] border-r bg-gray-50 p-4">
            <h3 className="font-semibold mb-3 text-gray-900">
              Quick Select
            </h3>
            <div className="space-y-1">
              {presetRanges.map((range) => (
                <button
                  key={range.key}
                  onClick={() => handlePresetSelect(range.key)}
                  className={`w-full text-left px-3 py-2 rounded text-sm transition-colors ${
                    selectedPreset === range.key
                      ? 'bg-blue-500 text-white'
                      : 'hover:bg-gray-200 text-gray-700'
                  }`}
                >
                  {range.label}
                </button>
              ))}
            </div>
          </div>

          {/* Right side - Native Date Inputs */}
          <div className="flex-1 p-6 w-full ">
            <h3 className="font-semibold mb-4 text-gray-900">
              Custom Range
            </h3>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Start Date
                </label>
                <input
                  type="date"
                  value={startDate}
                  onChange={(e) => {
                    setStartDate(e.target.value);
                    setSelectedPreset(null);
                  }}
                  className="w-full px-4 py-3 text-lg border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  End Date
                </label>
                <input
                  type="date"
                  value={endDate}
                  onChange={(e) => {
                    setEndDate(e.target.value);
                    setSelectedPreset(null);
                  }}
                  min={startDate}
                  className="w-full px-4 py-3 text-lg border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Submit button */}
        <div className="p-2 border-t">
          <div className="flex justify-end gap-2">
            <Button
              size="med"
              variant="ghost"
              onClick={() => setIsOpen(false)}
            >
              Cancel
            </Button>
            <Button
              size="med"
              onClick={handleSubmit}
              disabled={!selectedPreset && (!startDate || !endDate)}
              className="bg-gray-201 text-black hover:opacity-65 px-4"
            >
              Apply
            </Button>
          </div>
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default DateRangeModal;